import { HttpClient } from '@angular/common/http';
import { catchError, map, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { LEGACY_DATA_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import * as i0 from "@angular/core";
import * as i1 from "Angular/services/core/ajax.service";
import * as i2 from "@angular/common/http";
import * as i3 from "Angular/services/core/urls.service";
export class AccountsService {
    constructor(ajaxService, httpClient, urlsService) {
        this.ajaxService = ajaxService;
        this.httpClient = httpClient;
        this.urlsService = urlsService;
        // For anonymous users, skip API calls and return empty observables to improve page performance.
        if (window.USER.authenticated) {
            this.cachedUserPreferences = this.fetchUserPreferences();
            this.cachedClientGroupPreferences = this.fetchClientGroupPreferences();
        }
        else {
            this.cachedUserPreferences = of({});
            this.cachedClientGroupPreferences = of({});
        }
    }
    fetchUserPreferences() {
        const userPreferencesUrl = this.urlsService.ACCOUNTS.USER_PREFERENCES;
        return this.ajaxService.getDataObserveBody(userPreferencesUrl).pipe(map((preferences) => {
            if (preferences == null) {
                throw new Error(`Empty response body from ${userPreferencesUrl}`);
            }
            return preferences;
        }), 
        // shareReplay allows multiple subscribers to subscribe to this observable
        // without triggering additional HTTP requests.
        shareReplay(1));
    }
    fetchClientGroupPreferences() {
        const clientGroupPreferencesUrl = this.urlsService.ACCOUNTS.CLIENT_GROUP_PREFERENCES;
        return this.ajaxService.getDataObserveBody(clientGroupPreferencesUrl).pipe(map((preferences) => {
            if (preferences == null) {
                throw new Error(`Empty response body from ${clientGroupPreferencesUrl}`);
            }
            return preferences;
        }), 
        // shareReplay allows multiple subscribers to subscribe to this observable
        // without triggering additional HTTP requests.
        shareReplay(1));
    }
    getClientGroupPreferences() {
        return this.cachedClientGroupPreferences;
    }
    getUserPreferences() {
        return this.cachedUserPreferences;
    }
    getUserSecurityBenchmarkPreferences(security_id) {
        // For anonymous users, return empty default preferences
        if (!window.USER.authenticated) {
            return of({
                security_id,
                benchmark_name: '',
                benchmark_security_id: '',
                use_enhanced_benchmark: false,
            });
        }
        const url = this.urlsService.ACCOUNTS.USER_SECURITY_BENCHMARK_PREFERENCES;
        const getParams = { security_id };
        return this.ajaxService.getDataObserveBody(url, getParams);
    }
    postReferFriends(emails) {
        const url = this.urlsService.ACCOUNTS.REFER_FRIEND;
        const postParams = { emails };
        return this.ajaxService.postFormEncodedDataObserveResponse(url, postParams);
    }
    putUserPreferences(putParams) {
        const url = this.urlsService.ACCOUNTS.USER_PREFERENCES;
        return this.ajaxService.putDataObserveBody(url, putParams);
    }
    putUserSecurityBenchmarkPreferences(putParams) {
        const url = this.urlsService.ACCOUNTS.USER_SECURITY_BENCHMARK_PREFERENCES;
        return this.ajaxService.putDataObserveBody(url, putParams);
    }
    putUserPreferencesAndRefreshCache(putParams, errorCallback) {
        this.putUserPreferences(putParams).subscribe({
            next: () => {
                this.cachedUserPreferences = this.fetchUserPreferences();
            },
            error: errorCallback,
        });
    }
    updateProfile(profileData) {
        const url = this.urlsService.ACCOUNTS.ACCOUNT_PROFILE;
        return this.ajaxService.postFormEncodedDataObserveBody(url, profileData);
    }
    updatePassword(passwordData) {
        const url = this.urlsService.ACCOUNTS.CHANGE_PASSWORD;
        return this.ajaxService.postFormEncodedDataObserveBody(url, passwordData);
    }
    createChangeEmailRequest(changeEmailData) {
        const url = this.urlsService.ACCOUNTS.CHANGE_EMAIL;
        return this.ajaxService.postFormEncodedDataObserveBody(url, changeEmailData);
    }
    updateRiskScoreHypotheticalReturns(hypotheticalReturns, restoreToDefault = false) {
        const url = this.urlsService.ACCOUNTS.RISK_SCORE_BENCHMARK;
        return this.ajaxService.putJsonDataObserveBody(url, { benchmarks: hypotheticalReturns, restoreToDefault });
    }
    getMFADevices() {
        const url = this.urlsService.ACCOUNTS.MFA_DEVICE_COLLECTION;
        return this.ajaxService.getDataObserveBody(url);
    }
    manageMFADevices(postData) {
        const url = this.urlsService.ACCOUNTS.MFA_DEVICE_MANAGEMENT;
        return this.ajaxService.postFormEncodedDataObserveBody(url, postData);
    }
    useGenericData(overrides) {
        const requiredFeatures = [...(overrides ?? []), LEGACY_DATA_FEATURE];
        if (window.USER.features.find((feature) => requiredFeatures.includes(feature)) == null) {
            return of(true);
        }
        return this.getUserPreferences().pipe(map((preferences) => preferences.use_generic_data_display), catchError(() => of(true)));
    }
    changeAgentAddon(tierId) {
        const url = this.urlsService.ACCOUNTS.UPGRADE_AGENT_ADDON;
        return this.ajaxService.postFormEncodedDataObserveBody(url, {
            tier_id: String(tierId),
        });
    }
}
AccountsService.ɵfac = function AccountsService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AccountsService)(i0.ɵɵinject(i1.AjaxService), i0.ɵɵinject(i2.HttpClient), i0.ɵɵinject(i3.UrlsService)); };
AccountsService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AccountsService, factory: AccountsService.ɵfac, providedIn: 'root' });
